// A finding (spec §4): one researched claim with its sources, status and any web images. The body is
// shared by the card on a board and the Suggested tray.
import { FINDING_STATUSES, type Card, type FindingStatus } from '../../shared/doc.js';
import { updateCard } from '../../shared/ops.js';
import { assetUrl } from '../api.js';
import type { ProjectConn } from '../doc/useProject.js';

export type Finding = Extract<Card, { kind: 'finding' }>;

const STATUS_LABEL: Record<FindingStatus, string> = { researched: 'Researched (unverified)', confirmed: 'Confirmed', question: 'Question' };
const stop = (e: React.PointerEvent) => e.stopPropagation();
const host = (url: string) => {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
};

export function FindingBody({ card }: { card: Finding }) {
  return (
    <div className="finding">
      <span className={`status status-${card.status}`}>{STATUS_LABEL[card.status]}</span>
      <p className="claim">{card.claim}</p>
      {card.images.length > 0 && (
        <div className="finding-images">
          {card.images.map((img) => (
            <a key={img.url} href={img.sourcePageUrl ?? img.url} target="_blank" rel="noopener noreferrer" onPointerDown={stop}>
              {img.thumbAssetId ? <img src={assetUrl(img.thumbAssetId)} alt={img.credit || 'Image'} draggable={false} /> : <span>Image</span>}
              {img.credit && <small>{img.credit}</small>}
            </a>
          ))}
        </div>
      )}
      <ul className="sources">
        {card.sources.map((s, i) => (
          <li key={`${i}-${s.url}`}>
            <a href={s.url} target="_blank" rel="noopener noreferrer" onPointerDown={stop}>
              {s.title || host(s.url)}
            </a>{' '}
            <small className="muted">accessed {s.accessedAt.slice(0, 10)}</small>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** People set the status, including "confirmed" (agents are refused that on the server). */
export function StatusPicker({ conn, card }: { conn: ProjectConn; card: Finding }) {
  return (
    <label className="status-picker" onPointerDown={stop}>
      Status{' '}
      <select value={card.status} onChange={(e) => updateCard(conn.doc, conn.me, card.id, { status: e.target.value })}>
        {FINDING_STATUSES.map((s) => (
          <option key={s} value={s}>
            {STATUS_LABEL[s]}
          </option>
        ))}
      </select>
    </label>
  );
}

export function FindingCard({ conn, card, editing }: { conn: ProjectConn; card: Finding; editing: boolean }) {
  return (
    <>
      <FindingBody card={card} />
      {editing && <StatusPicker conn={conn} card={card} />}
    </>
  );
}
